/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { OverlayTrigger, Toast, ToastContainer, Tooltip } from 'react-bootstrap'
import './MessageCenterChatPreviewStyle.css'
import Button from '@components/common/Button/Button.component'
import { DropDownButtonComponent } from '@syncfusion/ej2-react-splitbuttons'
import { BeforeItemRenderEventArgs } from '@syncfusion/ej2-react-dropdowns'
import { ProgressBarComponent } from '@syncfusion/ej2-react-progressbar'
import { getFirstLineFromHTML } from '@utils/messageFormatter.util'
import { sanitizeStrictHtml } from '@utils/sanitize-html.util'
import IncomingCall from '../templates/IncomingCall/IncomingCall'
import CallEnded from '../templates/CallEnded/CallEnded'
import useChatPreview from './useChatPreview'
import {
  formatChatMessageTimestamp,
  getCallLog,
  getCompletedCallAudioUrl,
  getLastActive,
  getOutgoingChatStatus,
  getSenderType,
  messageCountsAsUnread,
} from '@utils/chat.util'
import { MenuComponent } from '@syncfusion/ej2-react-navigations'
import ConfirmBlockModal from '@components/layout/Modals/ConfirmBlockModal/ConfirmBlockModal.component'
import { AiReceptionistIcon, InlineTextEditor } from '@components/common'
import { ContentPreview } from '../templates'
import Avatar from '@components/common/Avatar/Avatar.component'

const MEMBERS_TOOLTIP_HIDE_DELAY_MS = 200

const ChatPreview: React.FC = () => {
  const { ...previewHooks } = useChatPreview()
  const menuContainerRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<MenuComponent>(null)
  const [lastCaretPosition, setLastCaretPosition] = useState<Range | null>(null)
  const [showMembersTooltip, setShowMembersTooltip] = useState(false)
  const hideMembersTooltipTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null)

  useEffect(() => {
    return () => {
      if (hideMembersTooltipTimeoutRef.current) {
        clearTimeout(hideMembersTooltipTimeoutRef.current)
      }
    }
  }, [])

  const saveCursorPosition = (): void => {
    const selection = document.getSelection()
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0)
      setLastCaretPosition(range.cloneRange())
    }
  }

  const restoreCursorPosition = useCallback((): void => {
    const selection = document.getSelection()
    if (lastCaretPosition && selection) {
      selection.removeAllRanges()
      selection.addRange(lastCaretPosition)
    }
  }, [lastCaretPosition])

  useEffect(() => {
    const editor = (
      previewHooks.toolbarRef.current as any
    )?.contentModule?.getEditPanel()

    const handleFocusOut = (): void => {
      saveCursorPosition()
    }

    const handleFocusIn = (): void => {
      restoreCursorPosition()
    }

    if (editor) {
      editor.addEventListener('focusout', handleFocusOut)
      editor.addEventListener('focusin', handleFocusIn)
    }

    return () => {
      if (editor) {
        editor.removeEventListener('focusout', handleFocusOut)
        editor.removeEventListener('focusin', handleFocusIn)
      }
    }
  }, [lastCaretPosition, previewHooks.toolbarRef, restoreCursorPosition])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent): void => {
      const target = e.target as HTMLElement

      // Check if clicked inside our menu wrapper
      const isInsideMenuRef = menuContainerRef.current?.contains(target)

      // Check if clicked inside Syncfusion's submenu (e.g., .e-menu-wrapper)
      const isInsideSyncfusionPopup = target.closest('.e-menu-wrapper') !== null

      if (!isInsideMenuRef && !isInsideSyncfusionPopup) {
        previewHooks.setIsKebabBtnActive(false)
        menuRef.current?.close()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const beforeItemRender = (args: BeforeItemRenderEventArgs): void => {
    const contactGroup =
      previewHooks.contactGroups && previewHooks.contactGroups.length
        ? previewHooks.contactGroups.find(
            (c) => c.contact_group_id === args.item.id,
          )
        : undefined
    let index = -1
    if (contactGroup && contactGroup.contacts)
      index = contactGroup.contacts.findIndex(
        (c) =>
          c.contact_id ===
          (previewHooks.participant &&
          previewHooks.participant.length === 1 &&
          previewHooks.participant[0].contact
            ? previewHooks.participant[0].contact.contact_id
            : ''),
      )

    if (previewHooks.selectedMessageState?.conversation_kind === 'group') {
      index = 0
    } else if (previewHooks.participant && previewHooks.participant.length > 1)
      index = -1

    if (
      ((args.item.id === 'unread' || args.item.id === 'read') &&
        previewHooks.selectedMessageState?.is_archived) ||
      (args.item.id === 'group' &&
        ((previewHooks.participant &&
          previewHooks.participant.length === 1 &&
          !previewHooks.participant[0].contact) ||
          (previewHooks.selectedMessageState?.conversation_kind === 'group' &&
            previewHooks.selectedMessageState?.name))) ||
      (args.item.id === 'block' &&
        previewHooks.participant &&
        previewHooks.participant.length > 1) ||
      (![
        'archive',
        'unarchive',
        'pin',
        'unpin',
        'group',
        'read',
        'unread',
        'block',
        'unblock',
        'delete',
        'create_group',
      ].includes(args.item.id as string) &&
        index !== -1)
    ) {
      args.element.classList.add('e-disabled')
    }
  }

  return (
    <>
      {previewHooks.selectedMessageState && (
        <div className="mcc-preview">
          <div className="convo-preview-header-container">
            <div className="d-flex align-items-center name-container">
              {!previewHooks.participant ||
              previewHooks.participant.length === 0 ||
              !previewHooks.participant[0].photo ? (
                <span className="mgc_user_3_fill no-profile-image" />
              ) : previewHooks.participant.length === 1 ? (
                <Avatar
                  source={previewHooks.participant[0].photo}
                  name={
                    previewHooks.participant[0].username ??
                    previewHooks.participant[0].phone_number
                  }
                  style={{ borderRadius: '12px' }}
                  width={40}
                  height={40}
                />
              ) : previewHooks.participant.length >= 2 &&
                previewHooks.participant[1].photo ? (
                <div className="multiple-participant-image">
                  <Avatar
                    source={previewHooks.participant[0].photo}
                    name={
                      previewHooks.participant[0].username ??
                      previewHooks.participant[0].phone_number
                    }
                    style={{ borderRadius: '12px' }}
                  />
                  <Avatar
                    source={previewHooks.participant[1].photo}
                    name={
                      previewHooks.participant[1].username ??
                      previewHooks.participant[1].phone_number
                    }
                    style={{ borderRadius: '12px' }}
                  />
                </div>
              ) : (
                <span className="mgc_user_3_fill no-profile-image" />
              )}
              <div className="d-flex flex-column">
                {previewHooks.selectedMessageState?.conversation_kind ===
                'group' ? (
                  <OverlayTrigger
                    placement="bottom"
                    show={showMembersTooltip}
                    onToggle={setShowMembersTooltip}
                    overlay={
                      <Tooltip id="convo-preview-members-tooltip">
                        <div
                          className="convo-preview-members-tooltip"
                          onMouseEnter={() => {
                            if (hideMembersTooltipTimeoutRef.current) {
                              clearTimeout(hideMembersTooltipTimeoutRef.current)
                              hideMembersTooltipTimeoutRef.current = null
                            }
                            setShowMembersTooltip(true)
                          }}
                          onMouseLeave={() => {
                            hideMembersTooltipTimeoutRef.current = setTimeout(
                              () => setShowMembersTooltip(false),
                              MEMBERS_TOOLTIP_HIDE_DELAY_MS,
                            )
                          }}
                        >
                          {previewHooks.selectedMessageState?.chat_participant?.map(
                            (p) => (
                              <div key={p.user_id ?? p.phone_number ?? ''}>
                                {p.username ?? p.phone_number}
                              </div>
                            ),
                          )}
                        </div>
                      </Tooltip>
                    }
                  >
                    <span
                      className="convo-preview-header"
                      onMouseEnter={() => {
                        if (hideMembersTooltipTimeoutRef.current) {
                          clearTimeout(hideMembersTooltipTimeoutRef.current)
                          hideMembersTooltipTimeoutRef.current = null
                        }
                        setShowMembersTooltip(true)
                      }}
                      onMouseLeave={() => {
                        hideMembersTooltipTimeoutRef.current = setTimeout(
                          () => setShowMembersTooltip(false),
                          MEMBERS_TOOLTIP_HIDE_DELAY_MS,
                        )
                      }}
                    >
                      {previewHooks.selectedMessageState?.name ??
                        previewHooks.participant
                          ?.map((p) => p.username ?? p.phone_number)
                          .join(', ')}
                    </span>
                  </OverlayTrigger>
                ) : (
                  <span className="convo-preview-header">
                    {previewHooks.selectedMessageState?.name ??
                      previewHooks.participant
                        ?.map((p) => p.username ?? p.phone_number)
                        .join(', ')}
                  </span>
                )}
                <small className="fst-italic custom-chat-preview-subheader">
                  {previewHooks.participant &&
                  previewHooks.participant.length === 1 &&
                  previewHooks.participant[0].last_active
                    ? getLastActive(previewHooks.participant[0].last_active)
                    : ''}
                </small>
              </div>
            </div>
            <div className="chat-heading-icon-container">
              <button
                data-bs-toggle="tooltip"
                title="Call"
                key="call-button-0"
                className="call-btn mgc_phone_fill"
                disabled={
                  !!(
                    previewHooks.currentCall ||
                    (previewHooks.participant &&
                      previewHooks.participant.length > 1)
                  )
                }
                onClick={previewHooks.makeCall}
              />
              <div className="more-options-container" ref={menuContainerRef}>
                <button
                  className={`chat-kebab-btn mgc_more_2_fill e-caret-hide${
                    previewHooks.isKebabBtnActive ? ' active' : ''
                  }`}
                  onClick={() =>
                    previewHooks.setIsKebabBtnActive(
                      !previewHooks.isKebabBtnActive,
                    )
                  }
                />
                {previewHooks.isKebabBtnActive && (
                  <div className="menu-container">
                    <MenuComponent
                      ref={menuRef}
                      orientation="Vertical"
                      select={previewHooks.handleMoreOptionsDropdown}
                      cssClass="chat-more-options"
                      showItemOnClick={true}
                      beforeItemRender={beforeItemRender}
                      items={[
                        previewHooks.selectedMessageState?.is_archived
                          ? {
                              text: 'Unarchive chat',
                              iconCss: 'mgc_unarchive_line',
                              id: 'unarchive',
                            }
                          : {
                              text: 'Archive chat',
                              iconCss: 'mgc_archive_line',
                              id: 'archive',
                            },
                        previewHooks.selectedMessageState?.is_pinned
                          ? {
                              text: 'Unpin chat',
                              iconCss: 'mgc_pin_line',
                              id: 'unpin',
                            }
                          : {
                              text: 'Pin chat',
                              iconCss: 'mgc_pin_fill',
                              id: 'pin',
                            },
                        {
                          text: 'Add to group',
                          iconCss: 'mgc_group_3_fill',
                          id: 'group',
                          items: [
                            {
                              text: 'Create group',
                              iconCss: 'mgc_group_3_fill',
                              id: 'create_group',
                            },
                            { separator: true },
                            ...(previewHooks.contactGroups.length > 0
                              ? previewHooks.contactGroups.map((group) => ({
                                  text: group.group_name,
                                  id: group.contact_group_id,
                                }))
                              : []),
                          ],
                        },
                        previewHooks.selectedMessageState?.chat_message?.some(
                          messageCountsAsUnread,
                        )
                          ? {
                              text: 'Mark read',
                              iconCss: 'mgc_mail_fill',
                              id: 'read',
                            }
                          : {
                              text: 'Mark unread',
                              iconCss: 'mgc_mail_open_fill',
                              id: 'unread',
                            },
                        previewHooks.isBlocked
                          ? {
                              text: 'Unblock chat',
                              iconCss: 'mgc_user_follow_fill',
                              id: 'unblock',
                            }
                          : {
                              text: 'Block chat',
                              iconCss: 'mgc_user_forbid_fill',
                              id: 'block',
                            },
                        {
                          text: 'Delete chat',
                          iconCss: 'mgc_delete_2_fill',
                          id: 'delete',
                        },
                      ]}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="chat-preview-row-2">
            {previewHooks.pinnedChat && (
              <div
                className="mcc-pinned-msg"
                role="button"
                tabIndex={0}
                aria-label="Jump to pinned message"
                onClick={previewHooks.scrollToPinnedChat}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter' && e.key !== ' ') return
                  if (e.target !== e.currentTarget) return
                  e.preventDefault()
                  previewHooks.scrollToPinnedChat()
                }}
              >
                <span className="pin-text">
                  <span className="mgc_pin_fill pin-icon" />
                  {getFirstLineFromHTML(previewHooks.pinnedChat.content)}
                </span>
                <button
                  type="button"
                  className="unstyled-button mgc_close_fill"
                  aria-label="Dismiss pinned message"
                  onClick={(e) => {
                    e.stopPropagation()
                    void previewHooks.handleCancelPinnedChat()
                  }}
                />
              </div>
            )}
            <div
              ref={previewHooks.chatRef}
              className="convo-container allow-user-select"
            >
              <div className="d-flex justify-content-center">
                <span className="start-convo-container">
                  Conversation started on{' '}
                  {
                    previewHooks.generateTimeDisplay(
                      new Date(
                        previewHooks.selectedMessageState
                          ?.date_time_created_at ?? new Date(),
                      ),
                    )[1]
                  }
                </span>
              </div>
              {previewHooks.chat?.map((data, index) => {
                return (
                  <div
                    className={`convo-msg-container ${data.chat_direction === 'Incoming' ? 'incoming-msg' : 'outgoing-msg'}${
                      previewHooks.highlightedMessageId === data.chat_message_id
                        ? ' convo-msg-container--deep-link-highlight'
                        : ''
                    }`}
                    key={index}
                    data-chat-message-id={data.chat_message_id}
                    ref={
                      previewHooks.lastReadMessageIndex === index
                        ? previewHooks.messagesEndRef
                        : undefined
                    }
                  >
                    <div className="sender-image-container">
                      {data.chat_direction === 'Incoming' ? (
                        !previewHooks.participant ||
                        (previewHooks.participant.length === 1 &&
                          !previewHooks.participant[0].photo) ? (
                          <span className="mgc_user_3_fill no-profile-image" />
                        ) : (
                          <Avatar
                            source={
                              previewHooks.participant[0].photo ??
                              'https://cdn-icons-png.flaticon.com/128/9131/9131478.png'
                            }
                            name={
                              previewHooks.participant[0].username ??
                              previewHooks.participant[0].phone_number
                            }
                            style={{ borderRadius: '12px' }}
                            width={40}
                            height={40}
                          />
                        )
                      ) : data.chat_direction === 'AI' ? (
                        <span className="ai-image">
                          <AiReceptionistIcon label="AI receptionist" />
                        </span>
                      ) : (
                        <Avatar
                          source="./clinic.png"
                          alt={
                            previewHooks.selectedMessageState?.name ??
                            previewHooks.participant
                              ?.map((p) => p.username ?? p.phone_number)
                              .join(', ')
                          }
                          style={{ borderRadius: '12px' }}
                          width={40}
                          height={40}
                        />
                      )}
                    </div>
                    {'is_deleted' in data && data.is_deleted ? (
                      <div className="deleted-chat">Message deleted</div>
                    ) : (
                      <div className="chat-content-container">
                        <span className="sender-name-time">
                          <span className="sender-name">
                            {data.sender?.first_name}
                          </span>
                          <span className="timestamp">
                            {data.is_pinned && (
                              <span className="mgc_pin_fill" />
                            )}
                            {formatChatMessageTimestamp(data.timestamp)}
                          </span>
                        </span>
                        <div
                          ref={
                            'is_pinned' in data && data.is_pinned
                              ? previewHooks.pinnedChatRef
                              : undefined
                          }
                          className="content-wrapper"
                        >
                          {[
                            '[CHAT-CALL-LOG]::{isCalling}',
                            '[CHAT-CALL-LOG]::{isRinging}',
                            '[CHAT-CALL-LOG]::{isInProgressCall}',
                          ].some((m) => data.content.includes(m)) ? (
                            <IncomingCall />
                          ) : data.content.includes(
                              '[CHAT-CALL-LOG]::{isCompletedCall}',
                            ) && data.chat_message_id ? (
                            <CallEnded
                              aiCallSummary={data.calls?.summary}
                              audioUrl={getCompletedCallAudioUrl(
                                data.content,
                                data.calls?.recording_url,
                              )}
                              audioTranscript={
                                data.calls?.transcript_text
                                  ? JSON.parse(
                                      data.calls?.transcript_text ?? '',
                                    )
                                  : undefined
                              }
                              chatId={data.chat_message_id as string}
                              showSummary={previewHooks.showSummary}
                              showTranscriptModal={
                                previewHooks.showTranscriptModal
                              }
                              setShowSummary={previewHooks.setShowSummary}
                              setShowTranscriptModal={
                                previewHooks.setShowTranscriptModal
                              }
                            />
                          ) : data.content.includes(
                              '[CHAT-CALL-LOG]::{isMissedCall}',
                            ) ? (
                            <span>
                              {previewHooks.participant &&
                              previewHooks.participant.length === 1 &&
                              previewHooks.participant[0].username
                                ? getCallLog(
                                    data.content,
                                    data.chat_direction,
                                    previewHooks.participant[0].username,
                                  )
                                : ''}
                            </span>
                          ) : (
                            <ContentPreview htmlContent={data.html_content} />
                          )}
                        </div>

                        {data.content.includes(
                          '[CHAT-CALL-LOG]::{isCalling}',
                        ) ||
                        data.content.includes('[CHAT-CALL-LOG]::{isRinging}') ||
                        data.content.includes(
                          '[CHAT-CALL-LOG]::{isInProgressCall}',
                        ) ||
                        data.content.includes(
                          '[CHAT-CALL-LOG]::{isCompletedCall}',
                        )
                          ? null
                          : (() => {
                              const statusLabel =
                                getSenderType(data.chat_direction) === 'client'
                                  ? `via ${previewHooks.selectedMessageState?.channel}`
                                  : getOutgoingChatStatus(
                                      data,
                                      previewHooks.trackMessageStatus,
                                    )
                              if (!statusLabel) return null
                              return (
                                <span className="channel-status">
                                  {statusLabel}
                                  {'is_edited' in data && data.is_edited && (
                                    <span className="mcc-edited-chat">
                                      edited
                                    </span>
                                  )}
                                </span>
                              )
                            })()}
                      </div>
                    )}
                    {!data.content.includes(
                      '[CHAT-CALL-LOG]::{isCompletedCall}',
                    ) && (
                      <DropDownButtonComponent
                        cssClass="e-caret-hide mcc-chat-action"
                        iconCss="ti ti-dots"
                        open={() => {
                          previewHooks.handleOpenChatDropdown(index)
                        }}
                        close={() => previewHooks.setActiveIndex(-1)}
                        select={previewHooks.handleChatMessageAction}
                        items={[
                          {
                            text:
                              'is_pinned' in data && data.is_pinned
                                ? 'Unpin'
                                : 'Pin',
                            iconCss: 'mgc_pin_fill',
                            id: 'pin',
                          },
                          ...(previewHooks.selectedMessageState?.channel ===
                            'SMS' || data.chat_direction === 'Incoming'
                            ? []
                            : [
                                {
                                  text: 'Edit',
                                  iconCss: 'mgc_pencil_fill',
                                  id: 'edit',
                                },
                              ]),
                          {
                            text: 'Copy',
                            iconCss: 'mgc_copy_2_line',
                            id: 'copy',
                          },
                          {
                            text: 'Delete message',
                            iconCss: 'mgc_delete_2_fill',
                            id: 'delete',
                          },
                        ]}
                      />
                    )}
                  </div>
                )
              })}
            </div>
            {previewHooks.isGenerating && previewHooks.isAiEnabled ? (
              <div className="generating-results-container">
                <div className="generating-results-header">
                  <span className="mgc_pencil_2_ai_fill" />
                  {previewHooks.aiAction === 'custom'
                    ? 'Generating results'
                    : previewHooks.aiAction}
                </div>

                <div className="generating-results-body">
                  {previewHooks.isLoading ? (
                    <ProgressBarComponent
                      id="circularIndeterminate"
                      type="Circular"
                      width="160px"
                      height="160px"
                      value={20}
                      enableProgressSegments={false}
                      isIndeterminate={true}
                      segmentCount={0}
                      trackThickness={5}
                      progressThickness={8}
                    />
                  ) : (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: sanitizeStrictHtml(previewHooks.aiResponse),
                      }}
                    />
                  )}
                </div>
                <div className="gr-buttons-container">
                  <div className="grbw grbw-left">
                    <button
                      type="button"
                      className="unstyled-button ai-button-text button-ai-regenerate"
                      onClick={() => previewHooks.handleSend()}
                    >
                      <span className="ti ti-refresh button-icon" />
                      Regenerate
                    </button>
                    <DropDownButtonComponent
                      select={previewHooks.handleAIDropDown}
                      className="ai-button-text ai-dropdown"
                      cssClass="generating-results-dropdown-elements"
                      items={[
                        {
                          text: 'Formalize',
                        },
                        {
                          text: 'Summarize',
                        },
                        {
                          text: 'List items',
                        },
                        {
                          text: 'Simplify',
                        },
                      ]}
                    >
                      <span className="mgc_magic_3_fill button-icon" />
                      Enhance
                    </DropDownButtonComponent>
                  </div>
                  <div className="grbw">
                    <Button
                      size="sm"
                      className="button-primary button-ai-insert"
                      onClick={previewHooks.handleInsertAIResponse}
                    >
                      Replace
                    </Button>
                  </div>
                </div>
              </div>
            ) : null}
            <div className="mcc-chat-input">
              {previewHooks.isEditing && (
                <div className="edit-label">Edit message</div>
              )}
              {previewHooks.isBlocked ? (
                <span className="unblock-message">
                  You’ve blocked this user. Messaging is currently disabled.{' '}
                  <button
                    type="button"
                    className="unstyled-button unblock-link"
                    onClick={() =>
                      previewHooks.setShowConfirmUnblockModal(true)
                    }
                  >
                    Unblock
                  </button>{' '}
                  to send message.
                </span>
              ) : previewHooks.isSmsMessagingDisabled ? (
                <span className="unblock-message">
                  SMS messaging is disabled for this clinic number. Enable it in
                  Settings → Communications → Messaging to send messages.
                </span>
              ) : (
                <InlineTextEditor
                  autoFocusKey={
                    previewHooks.selectedMessageState?.chat_conversation_id
                  }
                  aiAction={previewHooks.aiAction}
                  defaultPlaceholder='Type your message here or type "/" to enable AI functionality'
                  editorInput={previewHooks.editorInput}
                  isAiEnabled={previewHooks.isAiEnabled}
                  setAiAction={previewHooks.setAiAction}
                  setEditorInput={previewHooks.setEditorInput}
                  setIsAiEnabled={previewHooks.setIsAiEnabled}
                  toolbarRef={previewHooks.toolbarRef}
                  handleSend={previewHooks.handleSend}
                />
              )}
            </div>
          </div>
        </div>
      )}
      <div className="d-flex flex-column ">
        <ToastContainer
          position="bottom-center"
          className={`mcc-success-${previewHooks.isEditing}`}
        >
          <Toast show={Boolean(previewHooks.toastText)}>
            {previewHooks.toastText}
            {!previewHooks.toastText?.includes('copied') && (
              <span
                className="undo-delete"
                onClick={() =>
                  previewHooks.handleUndo(previewHooks.toastText || '')
                }
              >
                Undo
              </span>
            )}
          </Toast>
        </ToastContainer>
      </div>
      {previewHooks.showConfirmUnblockModal && (
        <ConfirmBlockModal
          show={previewHooks.showConfirmUnblockModal}
          onHide={() => {
            previewHooks.setShowConfirmUnblockModal(false)
          }}
          mainText="Unblock chat"
          subText="After unblocking, they will be able to message you again."
          action="Unblock"
          onConfirm={previewHooks.handleUnblockUser}
        />
      )}
    </>
  )
}

export default ChatPreview
